/**
 * The bulk bar and its confirm dialog.
 *
 * This is the most dangerous control in the console, so it is also the loudest. The confirm
 * shows the count, the breakdown by current status, and the notify state in words rather
 * than as a checkbox the eye can skip. Notify is off by default and the label says so.
 *
 * The cap is 50. CONTRACTS.md section 14.12 settled that against the support spec's 200.
 */
import { useState } from 'react';
import { Toggle, buttonStyle } from '@/features/customers/temp-ui';
import { useBulkApply, useBulkPreview, useBulkUndo } from './api';
import type { BulkPreview } from './types';

export const BULK_CAP = 50;

export function BulkBar({ selected, onDone }: { selected: number[]; onDone: () => void }) {
  const [action, setAction] = useState('resolve');
  const [notify, setNotify] = useState(false);
  const [preview, setPreview] = useState<BulkPreview | null>(null);
  const [undoJobId, setUndoJobId] = useState<string | null>(null);
  const previewMutation = useBulkPreview();
  const apply = useBulkApply();
  const undo = useBulkUndo();

  if (selected.length === 0 && undoJobId === null) return null;

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      style={{
        position: 'sticky',
        bottom: 0,
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: 'var(--space-3)',
        background: 'var(--surface-2)',
        borderTop: 'var(--border-width) solid var(--copper)',
      }}
    >
      {undoJobId ? (
        <>
          <span>Applied. You can undo for a few seconds.</span>
          <button
            type="button"
            style={buttonStyle('primary')}
            onClick={() => {
              undo.mutate(undoJobId, { onSettled: () => setUndoJobId(null) });
            }}
          >
            Undo
          </button>
          <button type="button" style={buttonStyle()} onClick={() => setUndoJobId(null)}>
            Dismiss
          </button>
        </>
      ) : (
        <>
          <strong data-testid="bulk-count">{selected.length} selected</strong>
          {selected.length > BULK_CAP ? (
            <span role="alert" style={{ color: 'var(--crit)' }}>
              A batch is capped at {BULK_CAP} tickets.
            </span>
          ) : null}
          <select aria-label="Bulk action" value={action} onChange={(event) => setAction(event.currentTarget.value)}>
            <option value="resolve">Resolve</option>
            <option value="set_status">Set status</option>
            <option value="assign">Assign</option>
            <option value="set_priority">Priority</option>
            <option value="add_tag">Tag</option>
          </select>
          <Toggle
            checked={notify}
            onChange={setNotify}
            label="Send a closing reply to the customer (off by default for bulk)"
          />
          <button
            type="button"
            style={buttonStyle('primary')}
            disabled={selected.length > BULK_CAP}
            onClick={() =>
              previewMutation.mutate(
                { action, ticket_ids: selected, params: {}, notify },
                { onSuccess: (result) => setPreview(result) },
              )
            }
          >
            Review
          </button>
        </>
      )}

      {preview ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${action} ${preview.count} tickets`}
          style={{
            position: 'fixed',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            background: 'var(--shadow-scrim)',
          }}
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const reason = String(new FormData(event.currentTarget).get('reason') ?? '');
              apply.mutate(
                { action, ticket_ids: selected, params: {}, notify, reason: reason || null },
                {
                  onSuccess: (job) => {
                    setPreview(null);
                    setUndoJobId(job.job_id);
                    onDone();
                  },
                },
              );
            }}
            style={{
              background: 'var(--surface-1)',
              border: 'var(--border-width) solid var(--copper)',
              borderRadius: 'var(--radius-card)',
              padding: 'var(--card-padding)',
              width: 'min(560px, 92vw)',
            }}
          >
            <h2 style={{ marginTop: 0 }} data-testid="bulk-confirm-title">
              {action === 'resolve' ? 'Resolve' : action.replace('_', ' ')} {preview.count} tickets
            </h2>

            <p data-testid="bulk-confirm-notify">
              {preview.notify
                ? 'A message will be sent to every selected customer.'
                : 'Silent change. No message is sent to any customer.'}
            </p>

            <ul style={{ color: 'var(--text-muted)', fontSize: 'var(--type-meta)' }}>
              {Object.entries(preview.by_status).map(([status, count]) => (
                <li key={status}>
                  {count} {status.replace('_', ' ')}
                </li>
              ))}
              <li>One audit event per ticket, undo for {preview.undo_seconds}s, cap {preview.cap}.</li>
              {preview.warnings.map((warning) => (
                <li key={warning} style={{ color: 'var(--warn)' }}>
                  {warning}
                </li>
              ))}
            </ul>

            <label style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
              <span className="caps-label">Reason</span>
              <input name="reason" style={{ display: 'block', width: '100%', padding: 'var(--space-2)' }} />
            </label>

            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
              <button type="button" style={buttonStyle()} onClick={() => setPreview(null)}>
                Cancel
              </button>
              <button type="submit" style={buttonStyle('primary')} disabled={apply.isPending}>
                {action === 'resolve' ? 'Resolve' : 'Apply to'} {preview.count}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
